import React from 'react';
import { useDispatch } from 'react-redux';
import { Dismiss20Regular, MoreHorizontal20Regular, Search12Regular } from '@fluentui/react-icons';
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import { toast } from 'react-toastify';
import { GetAuthenticatedDocUrl } from '../../../redux/documentsSlice';
import styles from '../css/vdr.module.css';
import { StatusDot } from '../components/StatusUI';
import {
    formatTranscriptDocument,
    formatTranscriptQuote,
    formatTranscriptSpeaker,
    formatTranscriptTime,
    normalizeRecordingTranscript,
    transcriptDownloadFilename,
} from '../liveData';

const ENABLE_MEETING_OBS_CREATE = false;

const MetaStack = ({ label, value }) => (
    <div className={styles.metaStack}>
        <div className={styles.metaStackLabel}>{label}</div>
        <div className={styles.metaStackValue}>{value ?? '—'}</div>
    </div>
);

const MeetingRecordingPanel = ({ recording, projectName = '', onClose, onDelete, onCreateObservation, onOpenRelated, relatedObs = [] }) => {
    const dispatch = useDispatch();
    const [tab, setTab] = React.useState('overview');
    const [transcriptQuery, setTranscriptQuery] = React.useState('');
    const [mediaUrl, setMediaUrl] = React.useState('');

    React.useEffect(() => {
        setTab('overview');
        setTranscriptQuery('');
        setMediaUrl('');
        const source = recording?.recordingFile;
        if (!source) return undefined;
        let cancelled = false;
        dispatch(GetAuthenticatedDocUrl(source))
            .unwrap()
            .then(url => {
                if (!cancelled) setMediaUrl(url || source);
            })
            .catch(() => {
                if (!cancelled) setMediaUrl(source);
            });
        return () => {
            cancelled = true;
        };
    }, [dispatch, recording?.id, recording?.recordingFile]);

    if (!recording) return null;

    const handleCopyLink = async () => {
        const link = `${window.location.origin}/dataroom?recording=${recording.id}`;
        try {
            await navigator.clipboard.writeText(link);
            toast.success('Link copied');
        } catch {
            toast.info(link);
        }
    };

    const snapshot = recording.aiSnapshot;
    const transcriptLines = normalizeRecordingTranscript(recording.transcript || []);
    const lines = transcriptLines.filter(line => {
        if (!transcriptQuery.trim()) return true;
        const q = transcriptQuery.toLowerCase();
        return `${line.speaker || ''} ${line.text || ''}`.toLowerCase().includes(q);
    });
    const names = recording.participantNames || [];
    const preview = names.slice(0, 3).join(', ');
    const moreCount = Math.max(0, (recording.participants || names.length) - 3);
    const participantsLabel = recording.participants
        ? `${recording.participants}${preview ? ` (${preview}${moreCount ? ` +${moreCount} more` : ''})` : ''}`
        : '—';
    const isAudio = /\.(mp3|m4a|wav|aac)(\?|$)/i.test(recording.recordingFile || '');

    const handleDownloadTranscript = () => {
        if (!transcriptLines.length) {
            toast.info('No transcript to download');
            return;
        }
        const blob = new Blob([formatTranscriptDocument(transcriptLines)], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = transcriptDownloadFilename({
            projectName,
            title: recording.title,
            date: recording.meetingDate,
        });
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleCreateObservation = () => {
        onCreateObservation?.(recording);
    };

    return (
        <aside className={styles.panel}>
            <div className={styles.panelHeader}>
                <div className={styles.panelHeaderMain}>
                    <h3 className={styles.panelTitle}>{recording.title}</h3>
                    <div className={styles.panelHeaderBadges}>
                        <StatusDot status={recording.status} />
                        {recording.source ? <span className={styles.sourcePill}>{recording.source}</span> : null}
                    </div>
                </div>
                <div className={styles.panelHeaderActions}>
                    <button type="button" className={styles.copyLinkBtn} onClick={handleCopyLink}>
                        Copy link
                    </button>
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <button type="button" className={styles.iconBtn} aria-label="More">
                                <MoreHorizontal20Regular />
                            </button>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem disabled={!transcriptLines.length} onClick={handleDownloadTranscript}>
                                    Download transcript
                                </MenuItem>
                                <MenuItem onClick={() => onDelete?.(recording)}>Delete recording</MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>
            </div>

            <div className={styles.panelBody}>
                <div className={styles.tabs}>
                    <button type="button" className={`${styles.tab} ${tab === 'overview' ? styles.tabActive : ''}`} onClick={() => setTab('overview')}>
                        Overview
                    </button>
                    <button type="button" className={`${styles.tab} ${tab === 'transcript' ? styles.tabActive : ''}`} onClick={() => setTab('transcript')}>
                        Transcript
                    </button>
                </div>

                {tab === 'overview' ? (
                    <>
                        <div className={styles.sectionBlock} style={{ marginTop: 0 }}>
                            <h4 className={styles.sectionHeading}>Playback</h4>
                            {!recording.recordingFile ? (
                                <p className={styles.subMeta}>No recording file (transcript-only meeting).</p>
                            ) : mediaUrl ? (
                                isAudio ? (
                                    <audio controls src={mediaUrl} style={{ width: '100%' }} />
                                ) : (
                                    <video controls src={mediaUrl} style={{ width: '100%' }} />
                                )
                            ) : (
                                <p className={styles.subMeta}>Loading recording…</p>
                            )}
                        </div>

                        {snapshot ? (
                            <div className={styles.sectionBlock}>
                                <h4 className={styles.sectionHeading}>AI quick snapshot</h4>
                                <div className={`${styles.snapshotBox} ${styles.snapshotContent}`}>
                                    {snapshot.keyTopics?.length > 0 && (
                                        <div className={styles.snapshotGroup}>
                                            <div className={styles.snapshotGroupTitle}>Key topics</div>
                                            <ul className={styles.snapshotList}>
                                                {snapshot.keyTopics.map(topic => (
                                                    <li key={topic}>{topic}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {snapshot.keyInformation?.length > 0 && (
                                        <div className={styles.snapshotGroup}>
                                            <div className={styles.snapshotGroupTitle}>Key information</div>
                                            <ul className={styles.snapshotList}>
                                                {snapshot.keyInformation.map(topic => (
                                                    <li key={topic}>{topic}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {ENABLE_MEETING_OBS_CREATE ? (
                                        <button type="button" className={styles.textLink} onClick={handleCreateObservation}>
                                            Create observation →
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        ) : transcriptLines.length > 0 ? (
                            <div className={styles.sectionBlock}>
                                <h4 className={styles.sectionHeading}>AI quick snapshot</h4>
                                <p className={styles.subMeta}>
                                    {recording.snapshotHydrationAttempted
                                        ? 'No summary could be built from this transcript. Open the Transcript tab to review the content.'
                                        : 'Generating summary from your transcript…'}
                                </p>
                            </div>
                        ) : null}

                        <div className={styles.sectionBlock}>
                            <h4 className={styles.sectionHeading}>Metadata</h4>
                            <MetaStack
                                label="Meeting date"
                                value={recording.meetingTime ? `${recording.meetingDate} · ${recording.meetingTime}` : recording.meetingDate}
                            />
                            <MetaStack label="Duration" value={recording.duration} />
                            <MetaStack label="Source" value={recording.source} />
                            {recording.meetingLink ? (
                                <MetaStack
                                    label="Meeting link"
                                    value={
                                        <a href={recording.meetingLink} target="_blank" rel="noopener noreferrer" className={styles.meetingLink}>
                                            {recording.meetingLink}
                                        </a>
                                    }
                                />
                            ) : null}
                            <MetaStack label="Added by" value={recording.addedBy} />
                            <MetaStack label="Participants" value={participantsLabel} />
                        </div>

                        <div className={styles.sectionBlock}>
                            <h4 className={styles.sectionHeading}>Related</h4>
                            <button
                                type="button"
                                className={styles.relatedRow}
                                style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left' }}
                                onClick={() => {
                                    if (relatedObs.length === 1) onOpenRelated?.(relatedObs[0]);
                                    else if (relatedObs.length > 1) onOpenRelated?.(null);
                                }}
                            >
                                <span>Observations</span>
                                <span className={styles.relatedCount}>{relatedObs.length} →</span>
                            </button>
                            {!snapshot && ENABLE_MEETING_OBS_CREATE ? (
                                <button type="button" className={styles.textLink} onClick={handleCreateObservation} style={{ marginTop: 8 }}>
                                    Create observation →
                                </button>
                            ) : null}
                        </div>
                    </>
                ) : (
                    <div className={styles.sectionBlock} style={{ marginTop: 0 }}>
                        <div className={styles.transcriptToolbar}>
                            <span className={styles.transcriptKindLabel}>Lens-generated transcript</span>
                            <label className={styles.transcriptSearch}>
                                <Search12Regular className={styles.transcriptSearchIcon} aria-hidden />
                                <input
                                    value={transcriptQuery}
                                    onChange={e => setTranscriptQuery(e.target.value)}
                                    placeholder="Search transcript"
                                    aria-label="Search transcript"
                                />
                            </label>
                        </div>
                        {transcriptLines.length === 0 ? (
                            <div className={styles.processingBox}>No transcript uploaded.</div>
                        ) : lines.length === 0 ? (
                            <div className={styles.emptyState}>No transcript matches</div>
                        ) : (
                            <div className={styles.transcriptList}>
                                {lines.map((line, i) => {
                                    const speaker = formatTranscriptSpeaker(line.speaker);
                                    const timeLabel = formatTranscriptTime(line.time);
                                    return (
                                        <div key={`${line.time}-${speaker}-${i}`} className={styles.transcriptLine}>
                                            <div className={styles.transcriptLineHead}>
                                                {timeLabel ? <span className={styles.transcriptTime}>{timeLabel}</span> : null}
                                                {speaker ? <span className={styles.transcriptSpeaker}>{speaker}</span> : null}
                                            </div>
                                            <p className={styles.transcriptText}>{formatTranscriptQuote(line.text)}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </aside>
    );
};

export default MeetingRecordingPanel;
